/* eslint-disable no-unused-vars */
/* eslint-disable no-negated-condition */
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  Switch,
  Divider,
  Stack,
  Container,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  Scale as ScaleIcon,
  ViewInAr as VolumeIcon,
  Inventory2 as BoxCountIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import VirtualBoxModal from "../components/modals/VirtualBoxModal";
import PackagingRulesModal from "../components/modals/PackagingRulesModal";
import Loader from "../components/Loader";
import { usePackageConfig } from "../customHooks/usePackageConfig";
import { PackageConfigState, VirtualBox } from "../interfaces/packageConfig";
import { useNavigate } from "react-router-dom";
import AiAdvisorPanel from "../components/AiAdvisorPanel";

/* ─────────────────────────────────────────────────────────────
   KPI Summary Strip
───────────────────────────────────────────────────────────── */
const KPIStrip: React.FC<{ state: PackageConfigState }> = ({ state }) => {
  const kpis = [
    {
      icon: <BoxCountIcon sx={{ fontSize: 18, color: "primary.main" }} />,
      label: "Cajas configuradas",
      value: state.config.virtual_boxes.length,
      unit: "",
      highlight: state.config.virtual_boxes.length === 0,
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 18, color: "text.secondary" }} />,
      label: "Máx. productos / caja",
      value: state.config.rules.max_units_per_parcel || "—",
      unit: state.config.rules.max_units_per_parcel ? "uds." : "",
    },
    {
      icon: <ScaleIcon sx={{ fontSize: 18, color: "text.secondary" }} />,
      label: "Peso máx. / caja",
      value: state.config?.rules?.max_weight_per_parcel?.toFixed(1) || "—",
      unit: state.config?.rules?.max_weight_per_parcel ? "kg" : "",
    },
    {
      icon: <VolumeIcon sx={{ fontSize: 18, color: "text.secondary" }} />,
      label: "Volumen máx. / caja",
      value: state.config?.rules?.max_volume_per_parcel?.toFixed(0) || "—",
      unit: state.config?.rules?.max_volume_per_parcel ? "cm³" : "",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 2,
        mb: 3,
        "@media (max-width: 900px)": { gridTemplateColumns: "repeat(2, 1fr)" },
        "@media (max-width: 600px)": { gridTemplateColumns: "1fr 1fr" },
      }}
    >
      {kpis.map((kpi, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: kpi.highlight ? "warning.light" : "divider",
            borderRadius: 2,
            bgcolor: kpi.highlight ? "warning.50" : "background.paper",
            transition: "border-color 0.2s",
          }}
        >
          <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
            {kpi.icon}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {kpi.label}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="baseline" gap={0.5}>
            <Typography
              variant="h5"
              fontWeight={700}
              color={kpi.highlight ? "warning.dark" : "text.primary"}
              lineHeight={1}
            >
              {kpi.value}
            </Typography>
            {kpi.unit && (
              <Typography variant="body2" color="text.secondary">
                {kpi.unit}
              </Typography>
            )}
          </Stack>
        </Paper>
      ))}
    </Box>
  );
};

/* ─────────────────────────────────────────────────────────────
   Box List Row
───────────────────────────────────────────────────────────── */
const BoxRow: React.FC<{
  box: VirtualBox;
  onEdit: (box: VirtualBox) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, active: boolean) => void;
}> = ({ box, onEdit, onDelete, onToggle }) => {
  const dims = `${box.max_width} × ${box.max_height} × ${box.max_depth}`;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 160px 100px 130px",
        alignItems: "center",
        px: 2.5,
        py: 1.75,
        gap: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        opacity: box.active ? 1 : 0.55,
        transition: "background 0.15s",
        "&:hover": { bgcolor: "action.hover" },
        "&:last-of-type": { borderBottom: "none" },
        "@media (max-width: 700px)": {
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "auto auto",
        },
      }}
    >
      {/* Name */}
      <Box>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ textDecoration: box.active ? "none" : "line-through" }}
        >
          {box.name}
        </Typography>
        {!box.active && (
          <Typography variant="caption" color="text.disabled">
            Inactiva
          </Typography>
        )}
      </Box>

      {/* Dimensions */}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={0.25}
        >
          Dimensiones (cm)
        </Typography>
        <Chip
          // eslint-disable-next-line prefer-template
          label={dims + " cm"}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontFamily: "monospace", fontSize: "0.72rem", height: 24 }}
        />
      </Box>

      {/* Weight */}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={0.25}
        >
          Peso máx.
        </Typography>
        <Chip
          label={`${box.max_weight} kg`}
          size="small"
          color="default"
          sx={{ fontSize: "0.72rem", height: 24, bgcolor: "action.selected" }}
        />
      </Box>

      {/* Actions */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Tooltip title="Editar caja">
          <IconButton
            size="small"
            color="primary"
            onClick={() => onEdit(box)}
            sx={{ "&:hover": { bgcolor: "primary.50" } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar caja">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(box._id)}
            sx={{ "&:hover": { bgcolor: "error.50" } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip
          title={
            box.active
              ? "Caja activa — clic para desactivar"
              : "Caja inactiva — clic para activar"
          }
        >
          <Switch
            checked={box.active}
            onChange={(e) => onToggle(box._id, e.target.checked)}
            color="primary"
            size="small"
          />
        </Tooltip>
      </Stack>
    </Box>
  );
};

const BoxesSection: React.FC<{
  state: PackageConfigState;
  onEditBox: (box: VirtualBox) => void;
  onDeleteBox: (id: string) => void;
  onToggleBox: (id: string, active: boolean) => void;
  onAdd: () => void;
}> = ({ state, onEditBox, onDeleteBox, onToggleBox, onAdd }) => {
  const boxes = state.config.virtual_boxes;
  const activeCount = boxes.filter((b) => b.active).length;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        mb: 3,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "primary.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InventoryIcon sx={{ fontSize: 18, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              Mis Cajas de Envío
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {boxes.length === 0
                ? "Sin cajas configuradas"
                : `${boxes.length} caja${boxes.length > 1 ? "s" : ""} · ${activeCount} activa${activeCount !== 1 ? "s" : ""}`}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none"}}
        >
          Agregar caja
        </Button>
      </Box>

      {/* Error */}
      {state.formErrors.virtualBoxes && (
        <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>
          {state.formErrors.virtualBoxes}
        </Alert>
      )}

      {/* Empty state */}
      {boxes.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "warning.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.5,
            }}
          >
            <WarningIcon sx={{ fontSize: 28, color: "warning.main" }} />
          </Box>
          <Typography variant="body1" fontWeight={600} color="text.primary">
            No hay cajas configuradas
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={360}>
            Definí al menos una caja para que el sistema pueda optimizar el
            embalaje y calcular costos logísticos precisos.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{ mt: 1, textTransform: "none", borderRadius: 1.5 }}
          >
            Agregar primera caja
          </Button>
        </Box>
      ) : (
        <>
          {/* Column headers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 160px 100px 130px",
              px: 2.5,
              py: 1,
              gap: 2,
              bgcolor: "grey.50",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {["Nombre", "Medidas", "Peso máx.", "Acciones"].map((h) => (
              <Typography
                key={h}
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {boxes.map((box) => (
            <BoxRow
              key={box._id}
              box={box}
              onEdit={onEditBox}
              onDelete={onDeleteBox}
              onToggle={onToggleBox}
            />
          ))}
        </>
      )}
    </Paper>
  );
};

/* ─────────────────────────────────────────────────────────────
   Packaging Rules Button
───────────────────────────────────────────────────────────── */
const PackagingRulesButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SettingsIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Límites de empaquetado
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reglas globales que aplican a todos los pedidos
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<SettingsIcon />}
          onClick={onClick}
          size="small"
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
        >
          Configurar límites
        </Button>
      </Box>
    </Paper>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────── */
const PackageConfiguration: React.FC = () => {
  const { state, actions } = usePackageConfig();
  const navigate = useNavigate();
  const [packagingRulesModalOpen, setPackagingRulesModalOpen] = useState(false);

  useEffect(() => {
    actions.getPackageConfiguration();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {/* ── Page Header ── */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <Box display="flex" alignItems="center" gap={1.5}>
              <ShippingIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Box>
                <Typography className="f-s-20">
                  Configuración de Embalaje
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Definí cajas y reglas para optimizar costos de envío
                </Typography>
              </Box>
            </Box>
            <Box flexGrow={1} />
            <Tooltip
              title={
                !state.hasUnsavedChanges
                  ? "Sin cambios pendientes"
                  : "Guardá los cambios para que apliquen en los próximos pedidos"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={actions.saveConfiguration}
                  disabled={!state.hasUnsavedChanges}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    minWidth: 160,
                  }}
                >
                  {state.hasUnsavedChanges ? "Guardar cambios" : "Sin cambios"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Container>
      </Box>

      {/* ── Page Content ── */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {state.saveConfigError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {state.saveConfigError}
          </Alert>
        )}

        {state.loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loader size="md" />
          </Box>
        ) : (
          <>
            {/* KPI strip */}
            <KPIStrip state={state} />

            {/* Boxes section (full width) */}
            <BoxesSection
              state={state}
              onEditBox={(box) => actions.openModal(box)}
              onDeleteBox={(id) => actions.deleteVirtualBox(id)}
              onToggleBox={(id, active) => actions.toggleVirtualBox(id, active)}
              onAdd={() => actions.openModal()}
            />

            {/* Rules form (narrower, centered) */}
            <Box>
              <PackagingRulesButton
                onClick={() => setPackagingRulesModalOpen(true)}
              />
            </Box>

            {/* AI Advisor Panel */}
            <AiAdvisorPanel state={state} />
          </>
        )}
      </Container>

      <VirtualBoxModal
        open={state.modalOpen}
        handleClose={actions.closeModal}
        virtualBox={state.editingBox || undefined}
        onSave={actions.saveVirtualBox}
      />

      <PackagingRulesModal
        open={packagingRulesModalOpen}
        handleClose={() => setPackagingRulesModalOpen(false)}
        rules={{
          max_units_per_parcel: state.config.rules.max_units_per_parcel || 0,
          max_weight_per_parcel: state.config.rules.max_weight_per_parcel || 0,
          max_volume_per_parcel: state.config.rules.max_volume_per_parcel || 0,
        }}
        onSave={(rules) => {
          actions.updateMaxUnitsPerParcel(rules.max_units_per_parcel);
          actions.updateMaxWeightPerParcel(rules.max_weight_per_parcel);
          actions.updateMaxVolumePerParcel(rules.max_volume_per_parcel);
        }}
        formErrors={state.formErrors}
      />
    </Box>
  );
};

export default PackageConfiguration;
